import React, { useEffect, useState, useRef } from "react";
import BlockKitStore from "../stores/BlockKitStore";
import { inject, observer } from "mobx-react";
import { AllCommunityModule, ICellRendererParams, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react'; 
import { CategoryJsonModel, ModeJsonModel } from "../models/BlockTransportSystemJsonModel";
import GlobalEnvironments from "../constants/GlobalEnvironments";
import { getLocaleString } from "../utils/DateHelper";
import { ExposureCategoryModel } from "../controllers/StudioAdminController";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { Button, Container } from "react-bootstrap";
import i18n from "../../shared/internationalization/I18nSetting";
import { useTranslation } from "react-i18next";

ModuleRegistry.registerModules([AllCommunityModule]);

const ImageCell = (props: ICellRendererParams) => {
    const imgSrc = `${GlobalEnvironments.getBlockTransportSystemHostUrlForWeb()}${props.value}`;
    return props.value ? (
    <a href={imgSrc} target="_blank" rel="noopener noreferrer">
        <img
        src={imgSrc}
        alt="thumbnail"
        style={{ width: "100%", height: "100%", objectFit: 'contain' }}
        />
    </a>
    ) : null;
};

export interface Props {
    blockKitStore?: BlockKitStore;
}

const PublishedCategoryPage: React.FC<Props> = inject("blockKitStore")(observer(({blockKitStore}) => {
    const [categories, setCategories] = useState<CategoryJsonModel[]>();
    const [modes, setModes] = useState<ModeJsonModel[]>();
    const [exposureCategories, setExposureCategories] = useState<ExposureCategoryModel[]>();
    const gridRef = useRef<AgGridReact<CategoryJsonModel>>(null);
    const { t } = useTranslation();

    // 그리드에서 직접 수정한 가격은 문자열로 들어올 수 있어 숫자로 변환해 검사한다. 비어 있으면 가격 없음으로 보고 허용한다.
    const isInvalidPrice = (price: unknown) =>
        price !== undefined && price !== null && price !== "" && !(Number(price) > 0);

    useEffect(() => {
        gridRef.current?.api?.refreshCells({ force: true });
    }, [blockKitStore?.timeZone]);

    const fetchCategoriesAsync = async () => {
        const response = await blockKitStore?.studioController.getCategoriesAsync()
        setCategories(response);
    }

    const fetchModesAsync = async () => {
        const response = await blockKitStore?.studioController.getModesAsync();
        setModes(response);
    }

    const fetchExposureCategoriesAsync = async () => {
        const response = await blockKitStore?.studioController.getExposureCategoriesAsync();
        setExposureCategories(response);
    }

    useEffect(() => {
        fetchCategoriesAsync();
        fetchModesAsync();
        fetchExposureCategoriesAsync();
    }, []);

    const NameCell: React.FC<ICellRendererParams<CategoryJsonModel>> = (params) => {
        const navigate = useNavigate();
        return (
            <span
                style={{ color: "blue", cursor: "pointer" }}
                onClick={() => navigate(`/published/${params.data?.id}`)}
            >
                {params.data?.lang.find(i => i.key === i18n.language)?.name ?? params.value}
            </span>
        );
    };

    const SaveButtonCell: React.FC<ICellRendererParams> = (params) => {
        return (
            <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                    if (isInvalidPrice(params.data?.price)) {
                        alert(t("price-invalid-message"));
                        return;
                    }

                    if (!confirm(t("edit-confirmation-message"))) {
                        return;
                    }

                    const response = await blockKitStore?.studioController.putCategoryAsync(params.data);
                    if (response?.isSuccess) {
                        alert(t("edit-success"));
                    } else {
                        alert(`${t("edit-failed")} (${response?.message})`);
                    }
                }}>
                    {t("button-save")}
            </Button>
        );
    }

    const DeleteButtonCell: React.FC<ICellRendererParams> = (params) => {
        return (
            <Button
                variant="danger"
                size="sm"
                onClick={async () => {
                    if (!confirm(`${t("delete-confirmation-message")}\n${t("delete-warning-message")}`)) {
                        return;
                    }

                    if (params.data.blockCount > 0) {
                        alert(t("delete-failed-usaging-message"));
                        return;
                    }

                    const response = await blockKitStore?.studioController.deletePublishedCategoryAsync(params.data);
                    if (response?.isSuccess) {
                        await fetchCategoriesAsync();
                        await fetchModesAsync();
                        await fetchExposureCategoriesAsync();
                        alert(t("delete-success"));
                    } else {
                        alert(`${t("delete-failed")} (${response?.message})`);
                    }
                }}>
                    {t("button-delete")}
            </Button>
        );
    }
    
    return (
        <Container fluid>
            <style>{`
                .ag-header-cell-label {
                    justify-content: center;
                }
            `}</style>
            <h4 className="m-2 pt-3 fw-bold">{t("published-category")}</h4>
            <div style={{ height: "80vh"}}>
                <AgGridReact<CategoryJsonModel>
                    ref={gridRef}
                    getRowId={params => params.data.id}
                    rowData={categories}
                    defaultColDef={{
                        cellStyle: { textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }
                    }}
                    columnDefs={[
                    {
                        headerName: t("delete"),
                        width: 80,
                        cellRenderer: DeleteButtonCell,
                    },
                    {
                        headerName: t("thumbnail"),
                        field: "thumbnailUrl",
                        width: 90,
                        cellRenderer: ImageCell,
                    },
                    {
                        headerName: t("name"),
                        field: "name",
                        filter: true,
                        cellRenderer: NameCell
                    },
                    {
                        headerName: t("description"),
                        field: "description",
                        filter: true,
                    },
                    {
                        headerName: t("price"),
                        field: "price",
                        width: 120,
                        filter: true,
                        editable: true
                    },
                    {
                        headerName: ("save"),
                        width: 80,
                        cellRenderer: SaveButtonCell,
                    },
                    {
                        headerName: t("priority"),
                        field: "priority",
                        width: 120,
                        filter: true,
                    },
                    {
                        headerName: t("mode"),
                        autoHeight: true,
                        filter: true,
                        cellStyle: {
                            "whiteSpaceCollapse": "preserve-breaks",
                            "lineHeight": 1.7
                        },
                        valueGetter: params => exposureCategories
                            ?.filter(i => i.categoryId === params.data?.id)
                            ?.map(i => modes?.find(m => m.id === i.modeId)?.name)
                            ?.sort()?.join("\n")
                    },
                    {
                        headerName: t("dedicated-product"),
                        field: "dedicatedProducts",
                        width: 180,
                        autoHeight: true,
                        filter: true,
                        cellStyle: {
                            "whiteSpaceCollapse": "preserve-breaks",
                            "lineHeight": 1.7
                        },
                        valueGetter: (params) => {
                            const arr = params.data?.dedicatedProducts ?? [];
                            return Array.isArray(arr) ? arr.sort().join("\n") : arr;
                        }
                    },
                    {
                        headerName: t("block-count"),
                        field: "blockCount",
                        filter: true,
                        width: 120,
                    },
                    {
                        headerName: t("id"),
                        field: "id",
                        filter: true
                    },
                    {
                        headerName: t("release-date"),
                        field: "releaseDate",
                        filter: "agDateColumnFilter",
                        valueFormatter: parmas => getLocaleString(new Date(parmas.data.releaseDate), blockKitStore.timeZone),
                    },
                    {
                        headerName: t("expire-date"),
                        field: "expireDate",
                        filter: "agDateColumnFilter",
                        valueFormatter: params => getLocaleString(new Date(params.data.expireDate), blockKitStore.timeZone)
                    },
                    ]}
                    rowSelection={{
                        mode: "singleRow",
                        checkboxes: false
                    }}
                    getRowStyle={params => {
                        const expireDate = new Date(params.data.expireDate);
                        const now = new Date();
                        if (expireDate < now) {
                            return { backgroundColor: "#ffe0e0" };
                        }
                        return null;
                        }
                    }
                />
            </div>
        </Container>
    )
}));

export default PublishedCategoryPage;